/**
 * Juwelier Reichart · interaktive Zeitleiste (Abschnitt jr-geschichte).
 * Beim Scrollen wird die Station aktiv, die die Bildschirmmitte erreicht hat: große Jahreszahl und „vor X Jahren“
 * wechseln mit, die goldene Linie füllt sich bis dorthin, die Sprungleiste markiert das Jahr.
 * Ohne JavaScript bleibt die Zeitleiste vollständig lesbar (Stile greifen erst mit der Klasse jr-zeitleiste-an).
 */

class JrZeitleiste extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bereit) return;
    this.dataset.bereit = '1';
    this.abschnitt = this.closest('.jr-geschichte');
    this.punkte = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.jr-geschichte__punkt')]);
    this.spruenge = /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('[data-sprung]')]);
    this.leiste = /** @type {HTMLElement} */ (this.querySelector('[data-leiste]'));
    this.jahrzahl = this.abschnitt?.querySelector('[data-jahrzahl]');
    this.abstand = this.abschnitt?.querySelector('[data-abstand]');
    if (!this.punkte.length || !this.leiste) return;
    this.abschnitt?.classList.add('jr-zeitleiste-an');
    this.aktiv = -1;
    this.ruhig = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.beimScrollen = () => {
      if (this.bild) return;
      this.bild = requestAnimationFrame(() => {
        this.bild = 0;
        this.messen();
      });
    };
    // Horizon scrollt am Desktop in .page-wrapper, am Handy im Fenster: Scrollen aller Elemente mithören
    document.addEventListener('scroll', this.beimScrollen, { passive: true, capture: true });
    addEventListener('resize', this.beimScrollen, { passive: true });

    // Klick in der Sprungleiste: genau dieses Jahr gilt, bis die Person selbst wieder scrollt
    // (sonst übernimmt am Seitenende die „letzte Station“-Regel und markiert ein anderes Jahr)
    this.spruenge.forEach((a, i) => {
      a.addEventListener('click', (e) => {
        const punkt = this.punkte[i];
        if (!punkt) return;
        e.preventDefault();
        this.gewaehlt = i;
        this.setzen(i);
        punkt.scrollIntoView({ behavior: this.ruhig ? 'auto' : 'smooth', block: 'center' });
        history.replaceState(null, '', a.getAttribute('href'));
      });
    });
    this.freigeben = () => {
      this.gewaehlt = undefined;
    };
    for (const art of ['wheel', 'touchmove', 'keydown']) addEventListener(art, this.freigeben, { passive: true });
    this.messen();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.beimScrollen, { capture: true });
    removeEventListener('resize', this.beimScrollen);
    for (const art of ['wheel', 'touchmove', 'keydown']) removeEventListener(art, this.freigeben);
    if (this.bild) cancelAnimationFrame(this.bild);
  }

  messen() {
    // Sprungleiste unter den mitlaufenden Kopf setzen
    const kopf = document.querySelector('header-component');
    const kopfUnten = kopf ? Math.max(0, kopf.getBoundingClientRect().bottom) : 0;
    this.style.setProperty('--jr-kopf', `${Math.round(kopfUnten)}px`);
    const mitte = innerHeight * 0.5;
    let aktiv = 0;
    this.punkte.forEach((p, i) => {
      if (p.getBoundingClientRect().top <= mitte) aktiv = i;
    });
    // Am Ende der Leiste erreicht die letzte Station die Mitte evtl. nie
    if (this.leiste.getBoundingClientRect().bottom < innerHeight * 0.8) aktiv = this.punkte.length - 1;
    if (this.gewaehlt !== undefined) aktiv = this.gewaehlt;
    const r = this.leiste.getBoundingClientRect();
    const fortschritt = Math.max(0, Math.min(1, (mitte - r.top) / Math.max(1, r.height)));
    this.leiste.style.setProperty('--jr-fortschritt', fortschritt.toFixed(3));
    if (aktiv !== this.aktiv) this.setzen(aktiv);
  }

  /** @param {number} index */
  setzen(index) {
    this.aktiv = index;
    this.punkte.forEach((p, i) => {
      p.classList.toggle('is-aktiv', i === index);
      p.classList.toggle('is-vorbei', i < index);
    });
    this.spruenge.forEach((a, i) => a.setAttribute('aria-current', String(i === index)));
    const jahr = this.punkte[index].dataset.jahr || '';
    const zahl = parseInt(jahr, 10);
    if (this.abstand) {
      const vor = new Date().getFullYear() - zahl;
      this.abstand.textContent = Number.isFinite(zahl) && vor > 0 ? `vor ${vor} Jahren` : '';
    }
    const anzeige = this.jahrzahl;
    if (!anzeige || anzeige.textContent === jahr) return;
    if (this.ruhig) {
      anzeige.textContent = jahr;
      return;
    }
    anzeige.classList.add('is-wechsel');
    setTimeout(() => {
      anzeige.textContent = jahr;
      anzeige.classList.remove('is-wechsel');
    }, 180);
  }
}

if (!customElements.get('jr-zeitleiste')) customElements.define('jr-zeitleiste', JrZeitleiste);
